'use client';

import { useEffect, useState } from 'react';
import useIsIframe from './use-is-iframe';
import useOverwolfApi from './use-overwolf-api';
import {
  ensureOverwolfStoreHandshake,
  subscribeOverwolfStoreReady,
  getInstalledAppIds,
  getInstalledElectronAppIds,
  isElectronAppInstalled,
  installApp,
  launchApp,
  launchElectronApp,
  uninstallApp,
  downloadApp,
  openSettings,
  placeOnClipboard,
} from '../services/overwolf/overwolf-store.service';
import type { OverwolfInstallOrigin } from '@ow/shared/store-bridge/bridge-channel';

interface UseOverwolfStoreHook {
  isAvailable: boolean;
  getInstalledAppIds: () => Promise<Set<string>>;
  getInstalledElectronAppIds: () => Promise<Set<string>>;
  isElectronAppInstalled: (uid: string) => Promise<boolean>;
  installApp: (uid: string, origin?: OverwolfInstallOrigin) => void;
  launchApp: (uid: string) => void;
  launchElectronApp: (uid: string) => void;
  uninstallApp: (uid: string) => void;
  downloadApp: (url: string, appId: string, appName?: string) => void;
  openSettings: (settingsTab?: string) => void;
  placeOnClipboard: (text: string) => void;
}

// -----------------------------------------------------------------------------
/**
 * Thin accessor for the Overwolf shell bridge (see overwolf-store.service). The
 * readiness handshake and all bridge traffic are centralized in the service, so
 * this hook only kicks off that one-time handshake and subscribes to its result
 * — it performs no per-component work. `isAvailable` reflects whether the shell
 * answered, after which install / launch / installed-state go over the bridge.
 */
export const useOverwolfStore = (): UseOverwolfStoreHook => {
  const isIframe = useIsIframe();
  const { isAvailable: isInOverwolfClient } = useOverwolfApi();
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    // The store bridge only applies when embedded as an iframe inside the
    // Overwolf client. Outside that (a plain browser, or a third-party embed
    // that isn't the Overwolf shell) we never start the handshake.
    if (typeof window === 'undefined' || !isIframe || !isInOverwolfClient) {
      setIsAvailable(false);
      return;
    }

    ensureOverwolfStoreHandshake();
    const unsubscribe = subscribeOverwolfStoreReady(setIsAvailable);

    return unsubscribe;
  }, [isIframe, isInOverwolfClient]);

  return {
    isAvailable,
    getInstalledAppIds,
    getInstalledElectronAppIds,
    isElectronAppInstalled,
    installApp,
    launchApp,
    launchElectronApp,
    uninstallApp,
    downloadApp,
    openSettings,
    placeOnClipboard,
  };
};

export default useOverwolfStore;
